import React from 'react';
import QRCode from 'react-qr-code';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  TextField,
} from '@mui/material';

import type { IDoctor } from '@app/interfaces/doctor.interface';
import { DoctorFormConfig, EMPTY_DOCTOR } from '@app/constants/doctor';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import IconButton from '@mui/material/IconButton';

const domain =
  process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://fb.respect-m.ru';

type Props = {
  doctor?: IDoctor;
  onSave: (doctor: Partial<IDoctor>) => void;
  onCancel: () => void;
};

function DoctorEditForm(props: Props) {
  const [doctor, setDoctor] = React.useState<Partial<IDoctor>>(props.doctor ?? EMPTY_DOCTOR);

  const [qrcode, setQrcode] = React.useState('');

  const [isLoading, setIsLoading] = React.useState(false);

  const changeDoctor = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDoctor({
      ...doctor,
      [field]: e.target.value,
    });
  };

  const saveDoctor = async () => {
    setIsLoading(true);
    await props.onSave(doctor);
    props.onCancel();
  };

  const onCancel = () => props.onCancel();

  const handleQrcodeClick = () => {
    const qrCodeUrl = `${domain}/qrcode/${doctor.id}`;
    setDoctor({
      ...doctor,
      qrCodeUrl,
    });
  };

  const handleQrCodeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDoctor({
      ...doctor,
      qrCodeUrl: e.target.value,
    })
  }

  const isValid = () =>
    DoctorFormConfig.filter((cnf) => cnf.required).every((cnf) => Boolean(doctor[cnf.field]));

  return (
    <>
      <Card sx={{ display: 'flex' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <CardMedia
            component='img'
            sx={{ width: 160 }}
            image={doctor?.imageUrl ? doctor?.imageUrl : '/assets/images/no-image.jpg'}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingBottom: '28px',
            }}
          >
            <Button disabled={isLoading} onClick={onCancel}>
              Отмена
            </Button>

            <Divider sx={{ height: 20 }} />

            <Button variant='contained' onClick={saveDoctor} disabled={!isValid() || isLoading}>
              Сохранить
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flex: '1 0 auto' }}>
            {DoctorFormConfig.map(({ label, field, ...props }, idx) => {
              return (
                <TextField
                  key={field}
                  autoFocus={idx === 0}
                  margin='dense'
                  id={field}
                  label={label}
                  fullWidth
                  value={doctor[field]}
                  onChange={changeDoctor(field)}
                  {...props}
                />
              );
            })}

            <FormControl sx={{ mt: 1 }} variant='outlined' fullWidth>
              <InputLabel htmlFor='qrcode-input'>QR Code</InputLabel>
              <OutlinedInput
                fullWidth
                id='qrcode-input'
                value={doctor.qrCodeUrl || ''}
                onChange={handleQrCodeChange}
                endAdornment={
                  <InputAdornment position='end'>
                    <Stack direction='row' spacing={1}>
                      <IconButton
                        aria-label='generate qrcode url'
                        onClick={handleQrcodeClick}
                        disabled={!doctor.id}
                        edge='end'
                      >
                        <AutoFixHighIcon color={doctor.id ? 'success' : 'disabled'} />
                      </IconButton>
                      <IconButton
                        aria-label='show qrcode url'
                        onClick={() => setQrcode(doctor.qrCodeUrl)}
                        edge='end'
                        disabled={!doctor.qrCodeUrl}
                      >
                        <QrCodeIcon color={doctor.qrCodeUrl ? 'primary' : 'disabled'} />
                      </IconButton>
                    </Stack>
                  </InputAdornment>
                }
                label='QR Code'
              />
            </FormControl>
          </CardContent>
        </Box>
      </Card>

      <Dialog open={!!qrcode}>
        <DialogTitle>{doctor.fio}</DialogTitle>
        <DialogContent dividers>
          <QRCode
            size={256}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            value={qrcode}
            viewBox={`0 0 256 256`}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setQrcode('')}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DoctorEditForm;
